import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import axios from "axios";

interface SiteOptionsContextType {
  ecommerceEnabled: boolean;
  loading: boolean;
  error: string | null;
  refreshOptions: () => Promise<void>;
}

const SiteOptionsContext = createContext<SiteOptionsContextType | undefined>(
  undefined
);

const DEFAULT_ECOMMERCE_ENABLED = true;

export const SiteOptionsProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [ecommerceEnabled, setEcommerceEnabled] = useState(
    DEFAULT_ECOMMERCE_ENABLED
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshOptions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/options");
      const enabled = response.data?.data?.ecommerce_enabled;
      setEcommerceEnabled(
        typeof enabled === "boolean" ? enabled : DEFAULT_ECOMMERCE_ENABLED
      );
      setError(null);
    } catch (err) {
      console.error("Failed to load site options:", err);
      // Fail open so storefront does not accidentally block purchases.
      setEcommerceEnabled(DEFAULT_ECOMMERCE_ENABLED);
      setError("Failed to load site options");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshOptions();
  }, [refreshOptions]);

  return (
    <SiteOptionsContext.Provider
      value={{ecommerceEnabled, loading, error, refreshOptions}}
    >
      {children}
    </SiteOptionsContext.Provider>
  );
};

export const useSiteOptions = () => {
  const context = useContext(SiteOptionsContext);
  if (context === undefined) {
    throw new Error("useSiteOptions must be used within a SiteOptionsProvider");
  }
  return context;
};
